import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowRight, Shield, Landmark, Users, Calendar, Lock, HelpCircle, Check } from 'lucide-react';

/**
 * COMMONS LANDING PAGE (PART A — FINAL LOCKED DIRECTIVE)
 * 
 * Required:
 * - What a Commons is
 * - Who it's for (communities, institutions, orgs)
 * - Steward responsibilities
 * - Scheduling & calendar coordination explanation
 * - Link to Commons FAQ
 * - "Create a Commons" CTA
 */
export default function CommonsLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-sm" style={{ backgroundColor: 'rgba(244, 239, 230, 0.95)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" 
                alt="LikeKind" 
                className="w-8 h-8 rounded-lg"
              />
              <span className="font-bold" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>LikeKind</span>
            </button>
            <Button 
              onClick={() => navigate('/auth?intent=commons')}
              size="sm"
              className="rounded-full"
              style={{ backgroundColor: 'var(--lk-earth-brown)', color: 'var(--lk-warm-sand)' }}
              data-testid="commons-enter-btn"
            >
              Create Commons
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-12">
        
        {/* What It Is */}
        <section className="mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
            A shared space for real communities.
          </h1>
          <p className="text-lg leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
            A Commons is a permanent community space on LikeKind — for neighborhoods, organizations, 
            institutions, and groups who want to coordinate activities, share resources, and stay connected.
          </p>
        </section>

        {/* Who It's For */}
        <section className="mb-12 p-6 rounded-2xl" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--lk-earth-brown)' }}>
              <Landmark className="w-5 h-5" style={{ color: 'var(--lk-warm-sand)' }} />
            </div>
            <h2 className="text-xl font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Who it&apos;s for</h2>
          </div>
          <ul className="space-y-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--lk-earth-brown)' }} />
              Neighborhood associations and HOAs
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--lk-earth-brown)' }} />
              Religious communities and congregations
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--lk-earth-brown)' }} />
              Nonprofits and community organizations
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--lk-earth-brown)' }} />
              Activity clubs and interest groups
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--lk-earth-brown)' }} />
              Schools and educational institutions
            </li>
          </ul>
        </section>

        {/* Steward Responsibilities */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>Steward responsibilities</h2>
          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            Every Commons requires at least two <strong>Stewards</strong> — responsible members who manage 
            the space and ensure safety. This dual-steward requirement prevents any single person from 
            having unchecked control.
          </p>
          <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
            <h3 className="font-medium mb-3" style={{ color: 'var(--fy-text-primary)' }}>Stewards are responsible for:</h3>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
                <span>Approving new members and managing access</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
                <span>Creating and managing Commons events</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
                <span>Ensuring community guidelines are followed</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
                <span>Responding to reports and safety concerns</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Scheduling & Calendar */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>Scheduling & calendar coordination</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(139, 106, 79, 0.15)' }}>
                <Calendar className="w-4 h-4" style={{ color: 'var(--lk-earth-brown)' }} />
              </div>
              <div>
                <h3 className="font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>Commons Calendar</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  A shared calendar for all Commons activities. Members can RSVP to events. 
                  Export to Google, Apple, or Outlook calendars.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(139, 106, 79, 0.15)' }}>
                <Users className="w-4 h-4" style={{ color: 'var(--lk-earth-brown)' }} />
              </div>
              <div>
                <h3 className="font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>Dual-approval for critical actions</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  Changes to event times, locations, or visibility require approval from both stewards. 
                  This protects the community from unilateral decisions.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(139, 106, 79, 0.15)' }}>
                <Lock className="w-4 h-4" style={{ color: 'var(--lk-earth-brown)' }} />
              </div>
              <div>
                <h3 className="font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>Privacy controls</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  Choose who can see your Commons: public (discoverable), members-only, or invitation-only. 
                  Participant names are private by default — only counts are shown.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Safety Promise */}
        <section className="mb-12 p-6 rounded-2xl" style={{ backgroundColor: 'var(--lk-sage-muted)', border: '1px solid var(--lk-primary-green)' }}>
          <div className="flex items-center gap-3 mb-3">
            <Shield className="w-5 h-5" style={{ color: 'var(--lk-primary-green)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--lk-primary-green)' }}>Safety promise</h2>
          </div>
          <ul className="text-sm space-y-2" style={{ color: 'var(--fy-text-primary)' }}>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>No algorithms. Events appear in chronological order.</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>No engagement tricks. No notifications designed to pull you back in.</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>If your Commons serves children, student safety rules automatically apply.</span>
            </li>
          </ul>
        </section>

        {/* CTAs */}
        <section className="space-y-4">
          <Button 
            onClick={() => navigate('/auth?intent=commons')}
            className="w-full h-14 rounded-xl text-lg font-semibold"
            style={{ backgroundColor: 'var(--lk-earth-brown)', color: 'var(--lk-warm-sand)' }}
            data-testid="commons-create-btn"
          >
            Create a Commons
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <Button 
            onClick={() => navigate('/faq/commons')}
            variant="outline"
            className="w-full h-12 rounded-xl font-medium"
            style={{ borderColor: 'var(--fy-border-subtle)', color: 'var(--fy-text-secondary)' }}
            data-testid="commons-faq-btn"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Commons FAQ
          </Button>
        </section>

        {/* Footer Links */}
        <footer className="mt-16 pt-8 flex flex-wrap gap-4 justify-center text-sm" style={{ borderTop: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-disabled)' }}>
          <button onClick={() => navigate('/for/individuals')} className="hover:underline">For Individuals</button>
          <span>·</span>
          <button onClick={() => navigate('/for/families')} className="hover:underline">For Families</button>
          <span>·</span>
          <button onClick={() => navigate('/for/schools')} className="hover:underline">For Schools</button>
          <span>·</span>
          <button onClick={() => navigate('/for/institutions')} className="hover:underline">For Institutions</button>
          <span>·</span>
          <button onClick={() => navigate('/privacy')} className="hover:underline">Privacy</button>
        </footer>
      </main>
    </div>
  );
}
